/*
Authentication primitives for the UX.

Owns:
  * Login / logout HTTP calls
  * Local JWT verification via jose + JWKS cache
  * Token storage (Vuex + localStorage; shared across tabs of the same origin)
  * Helpers consumed by interceptors, route guards and mixins.

The module deliberately keeps zero dependency on Vue components — it is
imported by the axios client, the Vuex store hydration, the route guard
and AccessMixins.
------------------------------------------------------------------------ */

import axios from 'axios'
import jose from '@/lib/jose-shim'

import { API_BASE_URL, JWT_ISSUER, JWT_AUDIENCE } from '@/lib/env'
import { getKey } from '@/lib/jwks'

export const ACCESS_STORAGE_KEY = 'xolas-jwt'
export const REFRESH_STORAGE_KEY = 'xolas-refresh'
export const POST_LOGIN_REDIRECT_KEY = 'xolas-post-login-redirect'

const LEEWAY_SECONDS = 30

let storeRef = null

/**
 * Wire the Vuex store. Called from main.js after the store is constructed.
 */
export function setStore (store) {
  storeRef = store
}

function getAuthSlice () {
  if (storeRef && storeRef.state && storeRef.state.auth) {
    return storeRef.state.auth
  }
  return null
}

function safeAuthStorage () {
  try {
    return (typeof window !== 'undefined' && window.localStorage) ? window.localStorage : null
  } catch (e) {
    return null
  }
}

function safeSessionStorage () {
  try {
    return (typeof window !== 'undefined' && window.sessionStorage) ? window.sessionStorage : null
  } catch (e) {
    return null
  }
}

/**
 * Validate an intended post-login redirect target.
 *
 * Contract: the returned value is only ever fed to `$router.push` (hash-mode,
 * same-origin in-app navigation). It must NOT be passed to `window.location`
 * or any sink that can leave the app — the accept-list below is sufficient for
 * a router-push consumer but is not a general-purpose URL sanitizer.
 *
 * Accepts only same-origin in-app paths (leading single '/'); rejects
 * protocol-relative ('//', '/\\'), schemes, control/whitespace chars, and the
 * '/login' and '/logout' routes (so we never bounce back into auth).
 *
 * @param {*} path candidate path (anything; non-strings rejected)
 * @returns {string|null} the path if safe, otherwise null
 */
export function sanitizeRedirectTarget (path) {
  try {
    if (!path || typeof path !== 'string') {
      return null
    }
    // Reject control chars and whitespace anywhere (legit router paths are
    // URL-encoded; raw spaces/tabs/newlines indicate a smuggling attempt).
    // eslint-disable-next-line no-control-regex -- control chars are exactly what we reject
    if (/[\u0000-\u001f\u007f\s]/.test(path)) {
      return null
    }
    // Must start with exactly one slash
    if (path.charAt(0) !== '/') {
      return null
    }
    // Reject protocol-relative: // or /\
    if (path.charAt(1) === '/' || path.charAt(1) === '\\') {
      return null
    }
    // Reject anything with a scheme (contains colon before first slash segment boundary)
    // e.g. javascript:alert(1) would be caught by the leading-slash check already,
    // but guard against edge cases by checking the first segment for a colon.
    var firstSegment = path.slice(1).split('/')[0]
    if (firstSegment.indexOf(':') !== -1) {
      return null
    }
    // Reject /login and /logout prefixes
    if (path === '/login' || path.indexOf('/login/') === 0 || path.indexOf('/login?') === 0) {
      return null
    }
    if (path === '/logout' || path.indexOf('/logout/') === 0 || path.indexOf('/logout?') === 0) {
      return null
    }
    return path
  } catch (e) {
    return null
  }
}

/**
 * Persist an intended post-login redirect path to sessionStorage.
 * Sanitizes the path first; rejected values are not stored.
 */
export function setPostLoginRedirect (path) {
  var safe = sanitizeRedirectTarget(path)
  if (!safe) {
    return
  }
  var ss = safeSessionStorage()
  if (ss) {
    try {
      ss.setItem(POST_LOGIN_REDIRECT_KEY, safe)
    } catch (e) {
      // ignore quota / private-mode failures
    }
  }
}

/**
 * Read and remove the post-login redirect path from sessionStorage.
 * Read-once: the key is always removed on first call.
 * Returns null if nothing was stored or the stored value is rejected by sanitizer.
 */
export function takePostLoginRedirect () {
  var ss = safeSessionStorage()
  if (!ss) {
    return null
  }
  var raw = null
  try {
    raw = ss.getItem(POST_LOGIN_REDIRECT_KEY)
    ss.removeItem(POST_LOGIN_REDIRECT_KEY)
  } catch (e) {
    return null
  }
  return sanitizeRedirectTarget(raw)
}

function buildUrl (path) {
  if (!path) {
    return path
  }
  if (/^https?:/i.test(path)) {
    return path
  }
  const base = API_BASE_URL || ''
  if (base && path.charAt(0) !== '/') {
    return base + '/' + path
  }
  return base + path
}

/**
 * Persist tokens to Vuex + localStorage and emit decoded claims.
 */
export function setTokens (tokens) {
  const access = tokens && tokens.accessToken ? tokens.accessToken : null
  const refresh = tokens && tokens.refreshToken ? tokens.refreshToken : null
  let claims = tokens && tokens.claims ? tokens.claims : null
  let expiresAt = tokens && tokens.expiresAt ? tokens.expiresAt : null

  if (access && !claims) {
    try {
      claims = jose.decodeJwt(access)
    } catch (e) {
      claims = null
    }
  }
  if (claims && !expiresAt && claims.exp) {
    expiresAt = Number(claims.exp) * 1000
  }

  const slice = getAuthSlice()
  if (slice) {
    slice.accessToken = access
    slice.refreshToken = refresh
    slice.claims = claims
    slice.expiresAt = expiresAt
  }

  const ss = safeAuthStorage()
  if (ss) {
    try {
      if (access) {
        ss.setItem(ACCESS_STORAGE_KEY, access)
      } else {
        ss.removeItem(ACCESS_STORAGE_KEY)
      }
      if (refresh) {
        ss.setItem(REFRESH_STORAGE_KEY, refresh)
      } else {
        ss.removeItem(REFRESH_STORAGE_KEY)
      }
    } catch (e) {
      // ignore
    }
  }

  return { accessToken: access, refreshToken: refresh, claims: claims, expiresAt: expiresAt }
}

/**
 * Wipe all in-memory and persisted auth state.
 */
export function clearTokens () {
  const slice = getAuthSlice()
  if (slice) {
    slice.accessToken = null
    slice.refreshToken = null
    slice.claims = null
    slice.expiresAt = null
  }
  const ss = safeAuthStorage()
  if (ss) {
    try {
      ss.removeItem(ACCESS_STORAGE_KEY)
      ss.removeItem(REFRESH_STORAGE_KEY)
    } catch (e) {
      // ignore
    }
  }
}

export function getAccessToken () {
  const slice = getAuthSlice()
  if (slice && slice.accessToken) {
    return slice.accessToken
  }
  const ss = safeAuthStorage()
  if (ss) {
    try {
      return ss.getItem(ACCESS_STORAGE_KEY)
    } catch (e) {
      return null
    }
  }
  return null
}

export function getRefreshToken () {
  const slice = getAuthSlice()
  if (slice && slice.refreshToken) {
    return slice.refreshToken
  }
  const ss = safeAuthStorage()
  if (ss) {
    try {
      return ss.getItem(REFRESH_STORAGE_KEY)
    } catch (e) {
      return null
    }
  }
  return null
}

export function getClaims () {
  const slice = getAuthSlice()
  if (slice && slice.claims) {
    return slice.claims
  }
  const token = getAccessToken()
  if (!token) {
    return null
  }
  try {
    return jose.decodeJwt(token)
  } catch (e) {
    return null
  }
}

/**
 * Truthy if there is a non-expired access token (with leeway).
 * Does NOT verify signature — that is `verifyToken`'s job.
 */
export function isAuthenticated () {
  const token = getAccessToken()
  if (!token) {
    return false
  }
  const claims = getClaims()
  if (!claims || !claims.exp) {
    return false
  }
  const nowSec = Math.floor(Date.now() / 1000)
  return (claims.exp + LEEWAY_SECONDS) > nowSec
}

/**
 * Cryptographically verify an access token against the JWKS.
 * Resolves with the verified payload; rejects on signature / iss / aud / exp failure.
 */
export function verifyToken (token) {
  if (!token) {
    return Promise.reject(new Error('verifyToken: no token'))
  }
  let header
  try {
    header = jose.decodeProtectedHeader(token)
  } catch (e) {
    return Promise.reject(e)
  }
  return getKey(header).then(function (key) {
    return jose.jwtVerify(token, key, {
      issuer: JWT_ISSUER,
      audience: JWT_AUDIENCE,
      algorithms: ['RS256'],
      clockTolerance: LEEWAY_SECONDS
    })
  }).then(function (result) {
    return result.payload
  })
}

/**
 * POST /api/login.
 * On success: verifies the access token, persists state, returns claims.
 */
export function login (credentials) {
  const url = buildUrl('/api/login')
  return axios.post(url, credentials, {
    headers: { 'Accept': 'application/json' }
  }).then(function (resp) {
    const data = resp && resp.data ? resp.data : {}
    const access = data.access_token
    const refresh = data.refresh_token
    if (!access) {
      throw new Error('login: response missing access_token')
    }
    return verifyToken(access).then(function (claims) {
      setTokens({ accessToken: access, refreshToken: refresh, claims: claims })
      return { user: data.user || null, claims: claims }
    })
  })
}

/**
 * POST /api/logout with the current refresh token, then clear state.
 * Always clears local state regardless of API response.
 *
 * Uses a bare axios instance (not the global one from src/lib/axiosClient.js)
 * so a 401 on logout cannot trigger the refresh+retry dance. The API
 * ignores an invalid Authorization header on logout anyway — the refresh
 * token in the body is the actual credential.
 */
export function logout () {
  const refresh = getRefreshToken()
  const access = getAccessToken()
  const url = buildUrl('/api/logout')
  const headers = { 'Accept': 'application/json' }
  if (access) {
    headers['Authorization'] = 'Bearer ' + access
  }
  const rawAxios = axios.create ? axios.create() : axios
  let req = Promise.resolve(null)
  if (refresh) {
    req = rawAxios.post(url, { refresh_token: refresh }, { headers: headers })
      .catch(function (err) { return err })
  }
  return req.then(function () {
    clearTokens()
    return true
  }, function () {
    clearTokens()
    return true
  })
}

/**
 * Exchange a refresh token for a new access + refresh pair.
 * Caller is expected to handle 401 (treat as session-over).
 */
export function refresh () {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return Promise.reject(new Error('refresh: no refresh token'))
  }
  const url = buildUrl('/api/auth/refresh')
  return axios.post(url, { refresh_token: refreshToken }, {
    headers: { 'Accept': 'application/json' }
  }).then(function (resp) {
    const data = resp && resp.data ? resp.data : {}
    const access = data.access_token
    const newRefresh = data.refresh_token || refreshToken
    if (!access) {
      throw new Error('refresh: response missing access_token')
    }
    let claims = null
    try {
      claims = jose.decodeJwt(access)
    } catch (e) {
      claims = null
    }
    return setTokens({ accessToken: access, refreshToken: newRefresh, claims: claims })
  })
}

/**
 * Read tokens from localStorage and repopulate Vuex. Intended for the
 * pre-mount hydration step in main.js.
 */
export function hydrateFromStorage () {
  const ss = safeAuthStorage()
  if (!ss) {
    return null
  }
  let access = null
  let refresh = null
  try {
    access = ss.getItem(ACCESS_STORAGE_KEY)
    refresh = ss.getItem(REFRESH_STORAGE_KEY)
  } catch (e) {
    return null
  }
  if (!access && !refresh) {
    return null
  }
  let claims = null
  if (access) {
    try {
      claims = jose.decodeJwt(access)
    } catch (e) {
      claims = null
    }
  }
  const slice = getAuthSlice()
  if (slice) {
    slice.accessToken = access || null
    slice.refreshToken = refresh || null
    slice.claims = claims
    slice.expiresAt = (claims && claims.exp) ? Number(claims.exp) * 1000 : null
  }
  return { accessToken: access, refreshToken: refresh, claims: claims }
}

export default {
  setStore: setStore,
  setTokens: setTokens,
  clearTokens: clearTokens,
  getAccessToken: getAccessToken,
  getRefreshToken: getRefreshToken,
  getClaims: getClaims,
  isAuthenticated: isAuthenticated,
  verifyToken: verifyToken,
  login: login,
  logout: logout,
  refresh: refresh,
  hydrateFromStorage: hydrateFromStorage,
  sanitizeRedirectTarget: sanitizeRedirectTarget,
  setPostLoginRedirect: setPostLoginRedirect,
  takePostLoginRedirect: takePostLoginRedirect
}
